import React from "react";
import { CircularProgress } from "@mui/material";
import { GiftIcon } from "@heroicons/react/24/outline";

/**
 * Handles all Place Order / Print Bill / Pay button logic for
 * retailType=RESTAURANT, branchSubType=FINE_DINE, saleMode=DINE_IN.
 *
 * kotPrintMode  : "ASK" | "ALWAYS" | "NEVER"
 * billPrintMode : "ASK" | "ALWAYS" | "NEVER"
 *
 * Phases:
 *  1. hasRequestedForNewItem && !showFineDineActionButtons  → Place Order (KOT)
 *  2. !hasRequestedForNewItem && !showFineDineActionButtons → Print Bill
 *  3. showFineDineActionButtons                             → Pay
 *
 *  complimentaryMode overrides all phases with complimentary buttons.
 */
const FineDineActionButtons = ({
  hasRequestedForNewItem,
  showFineDineActionButtons,
  complimentaryMode,
  disableProceed,
  noteFromCustLoader,
  kotPrintMode,
  billPrintMode,
  placeOrderLoader,
  printBillLoader,
  complementaryButtonLoader,
  selectedComplimentaryIds,
  markWholeCartComplimentary,
  isMobile,
  isBillPrinted,
  printedBillOrderId,
  handleTableReservation,
  handlePrintBill,
  handleOpenPaymentModal,
  handleComplimentaryCancel,
  handleComplimentaryApi,
}) => {
  // ─── Phase 1: Place Order (KOT) ──────────────────────────────────────
  const renderPlaceOrderButtons = () => {
    if (kotPrintMode === "ASK") {
      // Two buttons: "Print KOT & Order" (outline) + "Order" (filled)
      return (
        <div style={{ display: "flex", gap: "8px", width: "100%", minWidth: 0 }}>
          <button
            type="button"
            className="cart-btn-outline"
            style={{ width: "max-content", padding: "0.75rem 2rem", height: "auto" }}
            disabled={disableProceed || noteFromCustLoader || !!placeOrderLoader}
            onClick={() => handleTableReservation(false)}
            data-testid="button-print-kot-order"
          >
            {placeOrderLoader === "place" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-printer mr-2 h-4.5 w-4.5"
                  aria-hidden="true"
                >
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
                  <rect x="6" y="14" width="12" height="8" rx="1" />
                </svg>
                Print KOT &amp; Order
              </>
            )}
          </button>
          <button
            type="button"
            className="cart-btn-primary"
            style={{ width: "max-content", padding: "0.75rem 2rem", height: "auto" }}
            disabled={disableProceed || noteFromCustLoader || !!placeOrderLoader}
            onClick={() => handleTableReservation(true)}
            data-testid="button-order"
          >
            {placeOrderLoader === "skipKot" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Order"
            )}
          </button>
        </div>
      );
    }

    if (kotPrintMode === "ALWAYS") {
      // Always print — single "Print KOT & Order" button
      return (
        <div style={{ width: "100%" }}>
          <button
            type="button"
            className="cart-btn-primary"
            style={{ width: "100%" }}
            disabled={disableProceed || noteFromCustLoader || !!placeOrderLoader}
            onClick={() => handleTableReservation(false)}
            data-testid="button-print-kot-order-auto"
          >
            {placeOrderLoader ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-printer mr-2 h-4.5 w-4.5"
                  aria-hidden="true"
                >
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
                  <rect x="6" y="14" width="12" height="8" rx="1" />
                </svg>
                Print KOT &amp; Order
              </>
            )}
          </button>
        </div>
      );
    }

    // NEVER → single "Order" button, no print
    return (
      <div style={{ width: "100%" }}>
        <button
          type="button"
          className="cart-btn-primary"
          style={{ width: "100%" }}
          disabled={disableProceed || noteFromCustLoader || !!placeOrderLoader}
          onClick={() => handleTableReservation(true)}
          data-testid="button-order-never"
        >
          {placeOrderLoader ? <CircularProgress size={20} color="inherit" /> : "Order"}
        </button>
      </div>
    );
  };

  // ─── Phase 2: Print Bill ──────────────────────────────────────────────
  // "Payment" button handler — if order already created just open modal,
  // otherwise create order first (skipPrint=true) then open modal.
  const handlePaymentClick = () => {
    if (isBillPrinted && printedBillOrderId) {
      handleOpenPaymentModal();
    } else {
      handlePrintBill(true, true); // create order (skip print) + open payment modal
    }
  };

  const PaymentIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );

  const renderPrintBillButtons = () => {
    // NEVER → only Payment button, create order first then open modal
    if (billPrintMode === "NEVER") {
      return (
        <div style={{ width: "100%", minWidth: 0 }}>
          <button
            type="button"
            className="cart-btn-primary"
            style={{ width: "100%" }}
            disabled={disableProceed || !!printBillLoader}
            onClick={handlePaymentClick}
          >
            {printBillLoader === "skipPrint" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <PaymentIcon />
                <span>Payment</span>
              </>
            )}
          </button>
        </div>
      );
    }

    // ASK or ALWAYS → "Print bill" (outline) + "Payment" (filled)
    const printBillLabel = isBillPrinted ? "Reprint bill" : "Print bill";
    return (
      <div style={{ display: "flex", gap: "8px", width: "100%", minWidth: 0 }}>
        <button
          type="button"
          className="cart-btn-outline"
          style={{ width: "100%" }}
          disabled={disableProceed || !!printBillLoader}
          onClick={() => handlePrintBill(false)} // create order + print
        >
          {printBillLoader === "print" ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-printer mr-2 h-4.5 w-4.5"
                aria-hidden="true"
              >
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
                <rect x="6" y="14" width="12" height="8" rx="1" />
              </svg>
              {printBillLabel}
            </>
          )}
        </button>
        <button
          type="button"
          className="cart-btn-primary"
          style={{ width: "100%" }}
          disabled={disableProceed || !!printBillLoader}
          onClick={handlePaymentClick}
        >
          {printBillLoader === "skipPrint" ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <>
              <PaymentIcon />
              <span>Payment</span>
            </>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="dineInCheckOutButton">
      {/* Complimentary selection replaces all normal checkout actions. */}
      {complimentaryMode ? (
        <div className="complimentaryModeActionButton">
          <button
            type="button"
            className="cart-btn-outline"
            style={{ width: "50%" }}
            onClick={handleComplimentaryCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cart-btn-primary"
            aria-label="Add As Complimentary"
            style={{ width: "100%" }}
            disabled={
              (selectedComplimentaryIds.length === 0 && !markWholeCartComplimentary) ||
              complementaryButtonLoader
            }
            onClick={handleComplimentaryApi}
          >
            {complementaryButtonLoader ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <span>Add As Complimentary</span>
                <GiftIcon style={{ marginLeft: "8px", width: "16px", height: "16px" }} />
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Phase 1: Send new items to kitchen */}
          {hasRequestedForNewItem && !showFineDineActionButtons && renderPlaceOrderButtons()}

          {/* Phase 2: Print bill */}
          {!hasRequestedForNewItem && !showFineDineActionButtons && renderPrintBillButtons()}

          {/* Phase 3: Pay (after bill created) */}
          {showFineDineActionButtons && (
            <button
              type="button"
              className="cart-btn-primary"
              style={{ width: "100%" }}
              disabled={disableProceed}
              onClick={handleOpenPaymentModal}
            >
              Pay
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default FineDineActionButtons;
